"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { MenusContext } from "@core/helpers/client/contexts/MenusContext";
import { get_mega_menus_proxy } from "@core/services/megamenu";
import { useAuthContext } from "@core/helpers/client/contexts/AuthContext";

/**
 * Same context and value shape as core's MenusContextProvider (useMenusContext keeps working), plus
 * `initial_menus` from the server: those menus are in the server HTML and are not fetched again on mount.
 * The remaining keys are fetched after hydration as before, and everything is fetched again when the token changes.
 *
 * `menus_loading` stays true until that first fetch ends, as in core. A component that wants its seeded menu on the
 * server should check its own key (`menus?.[key] !== undefined`) instead of `menus_loading`.
 */
export const MenusProvider = ({ children, menuKeys = [], initial_menus }) => {
  const { token } = useAuthContext();
  const [menus, set_menus] = useState(() => initial_menus || {});
  const [menus_loading, set_menus_loading] = useState(true);
  const first_run = useRef(true);

  useEffect(() => {
    let cancelled = false;

    const seeded = first_run.current ? Object.keys(initial_menus || {}) : [];
    first_run.current = false;
    const keys = menuKeys.filter((key) => !seeded.includes(key));

    if (!keys.length) {
      set_menus_loading(false);
      return;
    }

    set_menus_loading(true);

    get_mega_menus_proxy({ menu_keys: keys }).then((result) => {
      if (cancelled) return;

      if (result?.status) {
        const fetched = {};
        result?.data?.forEach((item) => {
          fetched[item?.key] = item?.value;
        });
        set_menus((prev) => ({ ...prev, ...fetched }));
      }
      set_menus_loading(false);
    });

    return () => {
      cancelled = true;
    };
    // initial_menus only matters for the first run
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, menuKeys]);

  const value = useMemo(
    () => ({ menus, set_menus, menus_loading, set_menus_loading }),
    [menus, menus_loading]
  );

  return <MenusContext.Provider value={value}>{children}</MenusContext.Provider>;
};

export default MenusProvider;
